import React from 'react';
import { FaMinus, FaPlus, FaWhatsapp } from 'react-icons/fa';
import { BackIcon, Spinner, formatPrice } from './catalogueHelpers';

// Catalogue opened from the WhatsApp bot (?wa=<token>): no contact form here.
// The cart goes back to the WhatsApp conversation, where the bot asks where to
// deliver and the customer confirms.

const unitPrice = (item) => {
  const base = item.variant?.price ?? item.product.price ?? 0;
  return base + (item.options ?? []).reduce((s, o) => s + (o.price ?? 0) * (o.qty ?? 1), 0);
};

const WhatsAppButton = ({ href, children }) => (
  <a
    href={href}
    className="w-full inline-flex items-center justify-center gap-2 px-4 py-4 rounded-lg text-white font-bold bg-[#25D366] active:scale-[0.98] transition-all"
  >
    <FaWhatsapp className="w-5 h-5" />
    {children}
  </a>
);

export const WhatsAppCheckoutView = ({
  checkoutItems, checkoutTotal, checkoutCount,
  buyNowItems, setBuyNowItems, changeQuantity,
  businessName, isSubmitting, onSubmit, setView,
}) => (
  <div className="min-h-screen bg-white">
    <div className="bg-white px-4 sticky top-0 z-10">
      <div className="max-w-xl mx-auto flex items-center gap-1 py-4 border-b border-gray-200">
        <button
          onClick={() => { const wasBuyNow = buyNowItems !== null; setBuyNowItems(null); setView(wasBuyNow ? 'product' : 'catalogue'); }}
          className="p-2 -ml-2 text-gray-500 hover:text-gray-900 shrink-0"
        >
          <BackIcon />
        </button>
        <h1 className="text-lg font-bold text-gray-900 truncate">Votre panier</h1>
      </div>
    </div>

    <div className="max-w-xl mx-auto px-4 py-6 space-y-6">
      <div className="bg-gray-50 rounded-xl p-4 space-y-3">
        {checkoutItems.length === 0 && <p className="text-sm text-gray-500">Votre panier est vide.</p>}
        {checkoutItems.map(item => (
          <div key={item.product.id} className="flex items-center gap-3">
            {item.product.images?.[0]?.url ? (
              <img src={item.product.images[0].url} alt={item.product.name} className="w-12 h-12 object-cover rounded-lg flex-shrink-0" />
            ) : (
              <div className="w-12 h-12 bg-gray-100 rounded-lg flex-shrink-0" />
            )}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 truncate">{item.product.name}</p>
              {item.variant && <p className="text-xs text-gray-400">{item.variant.name}</p>}
              {(item.options ?? []).length > 0 && (
                <p className="text-xs text-gray-400 truncate">{item.options.map(o => (o.qty ?? 1) > 1 ? `×${o.qty} ${o.name}` : o.name).join(' • ')}</p>
              )}
              <p className="text-xs text-gray-500">{formatPrice(unitPrice(item))}</p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button onClick={() => changeQuantity(item.product.id, -1)} aria-label="Retirer un" className="w-7 h-7 rounded-full bg-gray-200 flex items-center justify-center text-gray-700 hover:bg-gray-300">
                <FaMinus className="w-3 h-3" />
              </button>
              <span className="w-5 text-center text-sm font-semibold">{item.quantity}</span>
              <button onClick={() => changeQuantity(item.product.id, 1)} aria-label="Ajouter un" className="w-7 h-7 rounded-full bg-gray-200 flex items-center justify-center text-gray-700 hover:bg-gray-300">
                <FaPlus className="w-3 h-3" />
              </button>
            </div>
          </div>
        ))}
      </div>

      <div className="rounded-xl border border-gray-200 px-4 py-3 space-y-2.5">
        <div className="flex justify-between text-sm">
          <span className="text-gray-600">Articles ({checkoutCount})</span>
          <span className="font-medium text-gray-900">{formatPrice(checkoutTotal)}</span>
        </div>
        <div className="flex justify-between pt-2.5 border-t border-gray-200">
          <span className="font-semibold text-gray-900">Total</span>
          <span className="font-bold text-gray-900">{formatPrice(checkoutTotal)}</span>
        </div>
      </div>

      <p className="text-sm text-gray-600">
        Ensuite, dans WhatsApp, {businessName} vous demande où livrer et vous confirmez la commande.
      </p>

      <button
        onClick={onSubmit}
        disabled={isSubmitting || checkoutItems.length === 0}
        className="w-full inline-flex items-center justify-center gap-2 text-white font-bold py-4 rounded-lg bg-[#25D366] transition-all active:scale-[0.98] disabled:opacity-50"
      >
        {isSubmitting ? <Spinner size="sm" /> : <><FaWhatsapp className="w-5 h-5" /><span>Valider sur WhatsApp</span></>}
      </button>
    </div>
  </div>
);

export const WhatsAppSentView = ({ whatsappUrl, businessName }) => (
  <div className="min-h-screen bg-white flex items-center justify-center px-6">
    <div className="max-w-xl w-full bg-white shadow-md rounded-2xl p-8 border border-gray-100 text-center space-y-4">
      <div className="mx-auto w-12 h-12 rounded-full bg-green-100 flex items-center justify-center text-green-600 text-2xl">✓</div>
      <h1 className="text-2xl font-bold text-gray-900">Panier envoyé !</h1>
      <p className="text-gray-600">
        Retournez sur WhatsApp : {businessName} vous demande où livrer, puis vous confirmez la commande.
      </p>
      {whatsappUrl && <WhatsAppButton href={whatsappUrl}>Retourner sur WhatsApp</WhatsAppButton>}
    </div>
  </div>
);

export const WhatsAppExpiredView = ({ whatsappUrl }) => (
  <div className="min-h-screen bg-white flex items-center justify-center px-6">
    <div className="max-w-xl w-full text-center space-y-4">
      <h1 className="text-2xl font-bold text-gray-900">Ce lien a expiré</h1>
      <p className="text-gray-600">
        Pour retrouver le catalogue, touchez à nouveau « Commander » dans la conversation WhatsApp.
      </p>
      {whatsappUrl && <WhatsAppButton href={whatsappUrl}>Ouvrir WhatsApp</WhatsAppButton>}
    </div>
  </div>
);
